import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  buildQueryParams,
  emptyValues,
  searchParamsToValues,
  valuesToSearchParams,
} from "../components/filters/filterUtils";

/**
 * Estado dos filtros avançados de uma listagem.
 *
 * - inicializa a partir da URL (links de filtro são compartilháveis);
 * - reflete mudanças de volta na URL sem empilhar histórico;
 * - debounce só nos campos de texto, para não disparar request a cada tecla;
 * - devolve `params` prontos para o axios e volta pra página 1 ao filtrar.
 */
const useAdvancedFilters = ({
  fields,
  debounceKeys = ["search"],
  debounceMs = 400,
  syncUrl = true,
  defaults = {},
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialisedRef = useRef(false);

  const blank = useMemo(() => emptyValues(fields), [fields]);

  const [values, setValues] = useState(() => ({
    ...blank,
    ...defaults,
    ...(syncUrl ? stripEmpty(searchParamsToValues(location.search, fields)) : {}),
  }));

  // Cópia com os campos de texto atrasados — é ela que alimenta a requisição.
  const [debouncedValues, setDebouncedValues] = useState(values);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValues(values), debounceMs);
    return () => clearTimeout(timer);
    // Campos não-texto também passam pelo timer, mas com o mesmo valor final;
    // o custo é imperceptível e o código fica com um caminho só.
  }, [values, debounceMs]);

  // Espelha na URL (replace, para o botão "voltar" não virar um histórico de
  // cada tecla digitada).
  useEffect(() => {
    if (!syncUrl) return;
    if (!initialisedRef.current) {
      initialisedRef.current = true;
      return;
    }
    const search = valuesToSearchParams(debouncedValues).toString();
    navigate({ search: search ? `?${search}` : "" }, { replace: true });
  }, [debouncedValues, navigate, syncUrl]);

  const reset = useCallback(() => {
    setValues({ ...blank, ...defaults });
  }, [blank, defaults]);

  const setValue = useCallback((key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const params = useMemo(
    () => buildQueryParams(fields, debouncedValues),
    [fields, debouncedValues]
  );

  return {
    values,
    setValues,
    setValue,
    reset,
    debouncedValues,
    params,
    // Chave estável para o react-query invalidar quando qualquer filtro muda.
    queryKey: JSON.stringify(params),
  };
};

/** Remove chaves vazias para não sobrescrever defaults com "". */
function stripEmpty(values) {
  return Object.entries(values).reduce((acc, [key, value]) => {
    const empty =
      value === undefined ||
      value === null ||
      value === "" ||
      (Array.isArray(value) && value.length === 0);
    if (!empty) acc[key] = value;
    return acc;
  }, {});
}

export default useAdvancedFilters;
